/**
 * Cart Toast - add-to-cart confirmation
 *
 * One shared toast for every add-to-cart path. Replaces the per-component
 * showToast() methods in variant-list-cards.js and quick-add-fun.js.
 *
 * Listens on document for:
 *   cart:item-added  detail: { title, variantTitle, price, image, quantity, url }
 *   cart:add-error   detail: { message }
 *   on:cart:add      dispatched by product-form.js; detail.cart is a full cart
 *
 * Repeat adds reuse this element: content swaps and the timer restarts, so
 * toasts never stack. That matters on product pages with 40 addable rows.
 */

if (!customElements.get('cart-toast')) {
  class CartToast extends HTMLElement {
    constructor() {
      super();
      this.hideTimer = null;
      this.onItemAdded = this.onItemAdded.bind(this);
      this.onAddError = this.onAddError.bind(this);
      this.onProductFormAdd = this.onProductFormAdd.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      this.card = this.querySelector('.cart-toast__card');
      this.thumbEl = this.querySelector('.cart-toast__thumb');
      this.statusEl = this.querySelector('.cart-toast__status-text');
      this.titleEl = this.querySelector('.cart-toast__title');
      this.variantEl = this.querySelector('.cart-toast__variant');
      this.priceEl = this.querySelector('.cart-toast__price');
      this.timerEl = this.querySelector('.cart-toast__timer');

      this.moneyFormat = this.dataset.moneyFormat || '${{amount_no_decimals}}';
      this.successText = this.dataset.successText || '已加入購物車';
      this.duration = parseInt(this.dataset.duration, 10) || 3400;

      document.addEventListener('cart:item-added', this.onItemAdded);
      document.addEventListener('cart:add-error', this.onAddError);
      document.addEventListener('on:cart:add', this.onProductFormAdd);

      // Hovering holds the toast open so it can be read and clicked.
      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => this.resume());
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', () => this.resume());
    }

    disconnectedCallback() {
      document.removeEventListener('cart:item-added', this.onItemAdded);
      document.removeEventListener('cart:add-error', this.onAddError);
      document.removeEventListener('on:cart:add', this.onProductFormAdd);
      document.removeEventListener('keydown', this.onKeydown);
      clearTimeout(this.hideTimer);
    }

    onItemAdded(evt) {
      this.show(evt.detail || {});
    }

    onAddError(evt) {
      this.showError((evt.detail && evt.detail.message) || '無法加入購物車');
    }

    /**
     * product-form.js dispatches on:cart:add with the full cart. Nothing else
     * gives that path any feedback, so derive the toast from the matching line.
     */
    onProductFormAdd(evt) {
      const detail = evt.detail || {};
      const cart = detail.cart;
      if (!cart || !Array.isArray(cart.items) || !cart.items.length) return;

      const item = cart.items.find((line) => line.variant_id === detail.variantId)
        || cart.items[0];

      this.show({
        title: item.product_title || item.title,
        variantTitle: item.variant_title,
        image: item.image || (item.featured_image && item.featured_image.url),
        price: item.final_price,
        quantity: item.quantity,
        url: item.url
      });
    }

    show(data) {
      const title = data.title || '';
      const variantTitle = this.cleanVariantTitle(data.variantTitle);
      const quantity = parseInt(data.quantity, 10) || 1;

      this.classList.remove('is-error');
      this.statusEl.textContent = this.successText;
      this.titleEl.textContent = title;
      this.titleEl.hidden = !title;

      const variantParts = [];
      if (variantTitle) variantParts.push(variantTitle);
      if (quantity > 1) variantParts.push(`${quantity} 件`);
      this.variantEl.textContent = variantParts.join(' ・ ');
      this.variantEl.hidden = variantParts.length === 0;

      if (typeof data.price === 'number') {
        this.priceEl.textContent = this.formatMoney(data.price * quantity);
        this.priceEl.hidden = false;
      } else {
        this.priceEl.hidden = true;
      }

      if (data.image) {
        this.thumbEl.style.backgroundImage = `url("${this.sizedImage(data.image)}")`;
        this.thumbEl.hidden = false;
      } else {
        this.thumbEl.style.removeProperty('background-image');
        this.thumbEl.hidden = true;
      }

      this.reveal();
    }

    showError(message) {
      this.classList.add('is-error');
      this.statusEl.textContent = message;
      this.titleEl.hidden = true;
      this.variantEl.hidden = true;
      this.priceEl.hidden = true;
      this.thumbEl.hidden = true;
      this.reveal();
    }

    reveal() {
      this.position();
      this.pulseCartBadge();

      // Restart the entry and countdown animations even mid-flight.
      this.classList.remove('is-visible', 'is-paused');
      void this.offsetWidth;
      this.classList.add('is-visible');

      document.addEventListener('keydown', this.onKeydown);

      clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => this.hide(), this.duration);
    }

    hide() {
      clearTimeout(this.hideTimer);
      this.classList.remove('is-visible', 'is-paused');
      document.removeEventListener('keydown', this.onKeydown);
    }

    pause() {
      if (!this.classList.contains('is-visible')) return;
      this.classList.add('is-paused');
      clearTimeout(this.hideTimer);
    }

    resume() {
      if (!this.classList.contains('is-visible')) return;
      this.classList.remove('is-paused');
      clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => this.hide(), 1200);
    }

    onKeydown(evt) {
      if (evt.key === 'Escape') this.hide();
    }

    /**
     * Align the toast under the header cart icon so the confirmation points at
     * the place the item went. The header is sticky, so its position is read
     * fresh on every show rather than cached.
     */
    position() {
      if (window.matchMedia('(max-width: 600px)').matches) {
        this.style.removeProperty('right');
        this.style.removeProperty('top');
        return;
      }

      const icon = document.querySelector('[data-cart-toggle]')
        || document.querySelector('header a[href$="/cart"]');
      if (!icon) return;

      const rect = icon.getBoundingClientRect();
      if (!rect.width && !rect.height) return;

      const right = Math.max(12, window.innerWidth - rect.right);
      this.style.right = `${right}px`;
      this.style.top = `${rect.bottom + 10}px`;
    }

    pulseCartBadge() {
      const badge = document.querySelector('[data-cart-toggle] [data-cart-count]')
        || document.querySelector('.header-hydrogen__cart-badge')
        || document.querySelector('#cart-icon-bubble');
      if (!badge) return;

      badge.classList.remove('cart-toast-pulse');
      void badge.offsetWidth;
      badge.classList.add('cart-toast-pulse');
      setTimeout(() => badge.classList.remove('cart-toast-pulse'), 500);
    }

    /** Shopify sends "Default Title" for single-variant products. */
    cleanVariantTitle(value) {
      if (!value || value === 'Default Title') return '';
      return value;
    }

    sizedImage(src) {
      if (!src || src.indexOf('cdn.shopify.com') === -1) return src;
      return src.indexOf('?') === -1 ? `${src}?width=120` : `${src}&width=120`;
    }

    /**
     * Render cents using the store's own money format, so the toast matches
     * prices rendered by Liquid's `money` filter elsewhere on the page.
     */
    formatMoney(cents) {
      const amount = cents / 100;
      const format = this.moneyFormat;
      const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
      const key = placeholder ? placeholder[1] : 'amount';

      let value;
      switch (key) {
        case 'amount_no_decimals':
          value = this.withCommas(Math.round(amount).toString());
          break;
        case 'amount_with_comma_separator':
          value = this.withPeriods(amount.toFixed(2)).replace('.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = this.withPeriods(Math.round(amount).toString());
          break;
        case 'amount_with_apostrophe_separator':
          value = amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'");
          break;
        default:
          value = this.withCommas(amount.toFixed(2));
      }

      return placeholder ? format.replace(placeholder[0], value) : `$${value}`;
    }

    withCommas(str) {
      const parts = str.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    }

    withPeriods(str) {
      const parts = str.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return parts.join('.');
    }
  }

  customElements.define('cart-toast', CartToast);
}
